import useSWR from "swr";
import { api } from "services/api.service";

interface NotionBlock {
  id: string;
  type: string;
  object: string;
  createdTime: string;
  lastEditedTime: string;
  archived: boolean;
  hasChildren: boolean;
  parent: any;
  [key: string]: any;
}

interface NotionBlocksResponse {
  results: NotionBlock[];
  nextCursor?: string;
  hasMore: boolean;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchNotionBlocks = async (pageId: string): Promise<NotionBlock[]> => {
  if (!pageId) {
    throw new Error("Article content is unavailable.");
  }

  const blocks: NotionBlock[] = [];
  let cursor: string | undefined = undefined;

  do {
    try {
      const response: { data: NotionBlocksResponse } = await api.get(`notion/blocks/${pageId}/children`, {
        params: cursor ? { startCursor: cursor } : {},
      });

      blocks.push(...(response.data.results || []));
      cursor = response.data.hasMore ? response.data.nextCursor : undefined;
    } catch (error: any) {
      const status = error?.response?.status;
      const retryAfter = Number(error?.response?.headers?.["retry-after"]) || 1000;

      if (status === 429) {
        await sleep(retryAfter);
        continue;
      }

      throw new Error("Failed to load article content. Please try again later.");
    }
  } while (cursor);

  return blocks;
};

export const useSupportArticleContent = (pageId: string | null) => {
  const { data, error, isLoading, mutate } = useSWR<NotionBlock[]>(
    pageId ? `notion-blocks-${pageId}` : null,
    () => fetchNotionBlocks(pageId!),
    {
      revalidateOnFocus: false,
      dedupingInterval: 10 * 60 * 1000,
      errorRetryCount: 2,
    }
  );

  return {
    blocks: data ?? [],
    isLoading,
    isError: Boolean(error),
    error,
    refresh: mutate,
  };
};

export { fetchNotionBlocks };
